import React, { useContext, useState, useEffect } from "react";
import SidePanel from "../side-panel";
import * as styles from "./styles.mod.scss";
import locale from "locales";
import GlobalContext from "services/global-context";
import { Formik } from "formik";
import TextInput from "../text-input";
import * as Yup from "yup";
import {
  createCustomer,
  createCustomerAccessToken, customerExist, setCustomerAccessToken
} from "services/shopify-account";
import { navigate } from "@reach/router";
import Show from "assets/images/icons/show.svg";
import Hide from "assets/images/icons/hide.svg";
import { trackSignUp, trackEmailAddress, trackError } from "helpers/analytics";
import hashEmailAddress from "helpers/hash-email-address";


const RegisterSchema = Yup.object().shape({
  first_name: Yup.string()
    .required(locale.register.requiredName),
  last_name: Yup.string()
    .required(locale.register.requiredLname),
  email: Yup.string().email(locale.register.invalidEmail).required(locale.register.confirmPsw),
  password: Yup
    .string()
    .required(locale.register.fillPsw)
    .matches(
      "(?=.{6,})",
      locale.register.pswValidation
    ),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), null], locale.register.matchPsw)
});

const Register = ({ wasOpened }) => {
  const [opened, setOpened] = useState(false);
  const [closing, setClosing] = useState(false);
  const { setCurrentPanel, getCustomerProfile } = useContext(GlobalContext);
  const loc = locale.register;
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPass, setShowConfPass] = useState(false);

  const showPass = () => setShowPassword(!showPassword);
  const showConfPassword = () => setShowConfPass(!showConfPass);

  const emailValid = (email) => {
    var re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleEmailOnBlur = (e) => {
    const email = e.target.value;
    if (emailValid(email)) {
      customerExist(email).then(res => {
        const customerState = res?.data?.customers?.edges[0]?.node?.state;
        if (customerState === "INVITED" || customerState === "DISABLED") {
          createCustomer({
            email,
            password: "testtest"
          }).then(response => {
            if (response?.data?.customerCreate?.customerUserErrors) {
              const activationNeeded = response.data.customerCreate?.customerUserErrors.find(k => k.code === "CUSTOMER_DISABLED");
              if (activationNeeded) {
                setCurrentPanel("activation", activationNeeded.message);
              }
            }
          });
        }
      }).catch(err => console.log(err));
    }
  };

  const handleSubmit = async ({ first_name, last_name, email, password }, { setFieldError }) => {
    setSubmitDisabled(true);
    createCustomer({
      firstName: first_name, lastName: last_name, email, password
    }).then((response) => {
      if (response.data.customerCreate?.customerUserErrors) {
        const exists = response.data.customerCreate?.customerUserErrors.find(k => k.code === "TAKEN");
        const activationNeeded = response.data.customerCreate?.customerUserErrors.find(k => k.code === "CUSTOMER_DISABLED");

        if (exists) {
          setFieldError("email", exists.message);
          trackError(exists.message);
          return;
        }
        else if (activationNeeded) {
          setCurrentPanel("activation", activationNeeded.message);
          return;

        }
      } else if (response.errors) {
        console.error(response.errors.map((err) => err.message));
        trackError(response.errors);
      }

      createCustomerAccessToken({
        email, password
      }).then((response) => {
        setSubmitDisabled(true);
        setCustomerAccessToken(response.data.customerAccessTokenCreate.customerAccessToken);
        getCustomerProfile(() => {
          navigate("/account/invite-friends", { state: { firstLogin: true } });
        });
        setCurrentPanel(null);

      });
    });

    trackSignUp("user account", "email address and password");
    const hashedEmail = await hashEmailAddress(email);
    trackEmailAddress(hashedEmail);
  };

  const handleLogin = () => {
    setCurrentPanel("login");
    return false;
  };

  useEffect(() => {
    setTimeout(() => !closing && setOpened(true), 0);
  });

  const handleClose = () => {
    setClosing(true);
    setOpened(false);
    setTimeout(() => setCurrentPanel(null), 250);
  };

  const isOpen = () => opened || (!closing && wasOpened);

  const trackRegisterError = (formErrors) => {
    const errorMessages = Object.values(formErrors).join("; ");
    if (errorMessages) {
      trackError(errorMessages);
    }
  };

  return <SidePanel
    open={true}
    bodyClass={styles.body}
    className={`${styles.sidePanel} ${isOpen() ? styles.opened : ""}`}
    onClose={handleClose}
  >
    <Formik
      initialValues={{
        first_name: "", last_name: "", email: "", password: "", confirm_password: ""
      }}
      onSubmit={handleSubmit}
      validationSchema={RegisterSchema}
    >
      {
        ({ errors, touched, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.formInner}>
              <h3>{loc.title}</h3>
              <p>{loc.subtitle}</p>
              <div className="row">
                <div className="col-12">
                  <TextInput type="text" name="email" onChange={handleChange} onBlur={handleEmailOnBlur} placeholder={loc.email} errors={errors.email} touched={touched.email} />
                </div>
                <div className="col-12">
                  <TextInput type="text" name="first_name" onChange={handleChange} placeholder={loc.first_name} errors={errors.first_name} touched={touched.first_name} />
                </div>
                <div className="col-12">
                  <TextInput type="text" name="last_name" onChange={handleChange} placeholder={loc.last_name} errors={errors.last_name} touched={touched.last_name} />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-lg-6">
                  <div className={styles.showPasswordIcon}>
                    <TextInput type={showPassword ? "text" : "password"} name="password" onChange={handleChange} placeholder={loc.password} errors={errors.password} touched={touched.password} />
                    <p onClick={showPass}>{showPassword ? <img src={Show} alt={locale.altText.showPassword} /> : <img src={Hide} alt={locale.altText.hidePassword} />}</p>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className={styles.showPasswordIcon}>
                    <TextInput type={showConfPass ? "text" : "password"} name="confirm_password" onChange={handleChange} placeholder={loc.confirm_password} errors={errors.confirm_password} touched={touched.confirm_password} />
                    <p onClick={showConfPassword}>{showConfPass ? <img src={Show} alt={locale.altText.showPassword} /> : <img src={Hide} alt={locale.altText.hidePassword} />}</p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <p className={styles.links}>
                    {loc.haveAccount} <a href="#" onClick={handleLogin}>{loc.login}</a>
                  </p>
                </div>
                <div className={styles.termsAndConditions}>
                  <p className={styles.terms}>
                    Mit Deiner Anmeldung akzeptierst Du die <b><a href="/agbs" target="_blank" rel="noreferrer">AGB </a></b>und die <b><a href="/club-account-teilnahmebedingungen" target="_blank" rel="noreferrer">Teilnahmebedingungen des Treueprogramms</a></b> und bestätigst, dass Du die Datenschutzrichtlinien sowie den Hinweis zum personalisierten E-Mail-Marketing zur Kenntnis genommen hast.
                    Das Angebot des Treueprogramms richtet sich nicht an Unternehmer im Sinne des § 14 BGB. Weitere Informationen findest Du dazu <a href="/club-account-teilnahmebedingungen" target="_blank" rel="noreferrer"><b>hier</b></a>.
                  </p>
                </div>
              </div>
            </div>
            <button type="submit" disabled={submitDisabled} className={styles.button} onClick={() => trackRegisterError(errors)}>{locale.register.register}</button>
          </form>
        )
      }

    </Formik>
  </SidePanel>;
};

export default Register;